import React, {Fragment, useRef, useState} from 'react';
import classNames from 'classnames';
import styles from '../styles.scss';

import { ConfigV1 } from '../../../types/config';
import { Sensor, Organization, CaptureStatus } from '../../../types/api';
import { ConfigStatus, ConfigType } from '../../../types/sensor-configuration';

import CaptureViewer, {CaptureViewerProps} from '../';
import CaptureCardHeader from './CaptureCardHeader';
import {
  convertToTimelineEvent,
  PlaybackState,
  ViewerPlaybackControls,
  Track,
} from '../ViewerPlaybackControls';
import Chip from '../../Chip';

import { Button } from '@densityco/ui';
import colorVariables from '@densityco/ui/variables/colors.json';
import { DEFAULT_MAX_X_OFFSET_MULTIPLIER } from '../../../utils/multi-point-installations';

export type CaptureViewerConfigState = {
  config: ConfigV1;
  configTemplate: ConfigType;
  status: ConfigStatus;
};

export type DualCaptureViewerProps = {
  leftSensor: {
    serialNumber: Sensor['serial_number'];
    index: number;
    organizationId: Organization['id'];
    configState: CaptureViewerConfigState;
    doorwayName: string;
  };
  leftCapture: null | CaptureViewerProps['capture'] & {
    status: CaptureStatus;
    tags: Array<{
      id: number;
      name: string;
      created_at: string;
      start_frame: number;
    }>;
    has_invalid_annotations: boolean,
    has_invalid_algo_events: boolean,
    latest_report: {
      annotated_events: Array<{
        id: any;
        direction: 1 | -1;
        seconds_offset: string;
        x_position: number;
        y_position: number;
      }>;
      algo_events: Array<{
        id: any;
        direction: 1 | -1;
        seconds_offset: string;
        x_position: number;
        y_position: number;
      }>;
    };
  };
  leftOffsetInSeconds: number;

  rightSensor: {
    serialNumber: Sensor['serial_number'];
    index: number;
    organizationId: Organization['id'];
    configState: CaptureViewerConfigState;
    doorwayName: string;
  };
  rightCapture: null | CaptureViewerProps['capture'] & {
    status: CaptureStatus;
    tags: Array<{
      id: number;
      name: string;
      created_at: string;
      start_frame: number;
    }>;
    has_invalid_annotations: boolean,
    has_invalid_algo_events: boolean,
    latest_report: {
      annotated_events: Array<{
        id: any;
        direction: 1 | -1;
        seconds_offset: string;
        x_position: number;
        y_position: number;
      }>;
      algo_events: Array<{
        id: any;
        direction: 1 | -1;
        seconds_offset: string;
        x_position: number;
        y_position: number;
      }>;
    };
  };
  rightOffsetInSeconds: number;

  distanceInInches: number;
  overlappingRegionWidth?: number /* this is the width of the both green areas in both captures summed */;
  maxXOffsetMultiplier?: number /* this value repredents the ratio of the overlapping region width that is used when deduplicating*/;

  configState: CaptureViewerProps['configState'];
  initialStartTimeSeconds?: CaptureViewerProps['initialStartTimeSeconds'];
  keyboardShortcutsEnabled: boolean;
  onVideoRefChanged?: (videoRef: HTMLVideoElement) => void;
};

function CaptureErrors({capture}) {
  return (<div className={styles.captureErrorWrapper}>
    {capture.has_invalid_annotations ?
      (<div className={styles.captureError}>
        There are annotations on this capture that are missing x,y coordinates
      </div>)
    : null }

    {capture.has_invalid_algo_events ?
      (<div className={styles.captureError}>
        There are algo events on this capture that are missing x,y coordinates
      </div>)
    : null }
  </div>
  )
};

export function approximatelyEqual(a, b) {
  return Math.abs(a - b) < 0.03;
}

const DualCaptureViewer: React.FunctionComponent<DualCaptureViewerProps> = ({
  leftSensor,
  leftCapture,

  rightSensor,
  rightCapture,

  leftOffsetInSeconds,
  rightOffsetInSeconds,

  distanceInInches,
  overlappingRegionWidth = 0,
  maxXOffsetMultiplier = DEFAULT_MAX_X_OFFSET_MULTIPLIER,
  initialStartTimeSeconds = 0,
  keyboardShortcutsEnabled,
  onVideoRefChanged,
}) => {
  const leftRef = useRef<HTMLVideoElement>(null);
  const rightRef = useRef<HTMLVideoElement>(null);

  const [playbackState, setPlaybackState] = useState<PlaybackState>({
    seek: initialStartTimeSeconds,
    bufferedRanges: [],
  });

  const rerender = useState(0)[1];
  function rerenderDueToRefChange() {
    rerender(Math.random());
  }

  const leftAnnotationEvents = leftCapture && leftCapture.latest_report
    ? convertToTimelineEvent(leftCapture.latest_report.annotated_events)
    : [];
  const leftAlgoEvents = leftCapture && leftCapture.latest_report
    ? convertToTimelineEvent(leftCapture.latest_report.algo_events)
    : [];
  const rightAnnotationEvents = rightCapture && rightCapture.latest_report
    ? convertToTimelineEvent(rightCapture.latest_report.annotated_events)
    : [];
  const rightAlgoEvents = rightCapture && rightCapture.latest_report
    ? convertToTimelineEvent(rightCapture.latest_report.algo_events)
    : [];

  // Figure out if an event on the left and/or right sensor track is selected.
  const leftPoint = leftRef.current
    ? [...leftAnnotationEvents, ...leftAlgoEvents].find(i =>
        approximatelyEqual(
          i.secondsOffset,
          playbackState.seek - leftOffsetInSeconds,
        ),
      )
    : undefined;

  const rightPoint = rightRef.current
    ? [...rightAnnotationEvents, ...rightAlgoEvents].find(i =>
        approximatelyEqual(
          i.secondsOffset,
          playbackState.seek - rightOffsetInSeconds,
        ),
      )
    : undefined;

  // Figure out the time that playback would end if the tracks were played
  // at their given offsets, and that's the track length.
  let trackLengthInSeconds: number = 0;

  if (leftCapture && rightCapture) {
    trackLengthInSeconds =
      leftRef.current &&
      rightRef.current &&
      !isNaN(leftRef.current.duration) &&
      !isNaN(rightRef.current.duration)
        ? Math.min(
            leftRef.current.duration - leftOffsetInSeconds,
            rightRef.current.duration - rightOffsetInSeconds,
          )
        : 0;
  } else if (
    leftCapture &&
    leftRef.current &&
    !isNaN(leftRef.current.duration)
  ) {
    trackLengthInSeconds = leftRef.current.duration - leftOffsetInSeconds;
  } else if (rightCapture && rightRef.current && !isNaN(rightRef.current.duration)) {
    trackLengthInSeconds = rightRef.current.duration - rightOffsetInSeconds;
  }

  // construct the tracks array based on the existence of captures, per side
  let tracks: Array<Track> = [];
  if (leftCapture) {
    tracks.push({
      serialNumber: leftSensor.serialNumber,
      captureStatus: leftCapture.status,
      primary: true,
      videoRef: leftRef.current,
      offsetInSeconds: leftOffsetInSeconds,
      annotationEvents: leftAnnotationEvents,
      algoEvents: leftAlgoEvents,
    });
  }
  if (rightCapture) {
    tracks.push({
      serialNumber: rightSensor.serialNumber,
      captureStatus: rightCapture.status,
      primary: true,
      videoRef: rightRef.current,
      offsetInSeconds: rightOffsetInSeconds,
      annotationEvents: rightAnnotationEvents,
      algoEvents: rightAlgoEvents,
    });
  }

  const noCaptureNode = (
    <div
      className={classNames([
        styles.captureCardCaptureViewer,
        styles.captureCardCaptureEmpty,
      ])}
      data-test-id="mpi-dual-viewer-less-than-one-capture"
    >
      <h5>No captures for this sensor found in this MPI group capture.</h5>
      <p>Refer to other sensor(s) for immediate reference captures.</p>
    </div>
  );

  return (
    <div className={styles.dualCaptureViewer}>
      <div className={styles.captureCardList}>
        <div className={styles.captureCardListInner}>
          <div className={styles.captureCard}>
            <CaptureCardHeader
              sensor={leftSensor}
              capture={leftCapture}
              seek={playbackState.seek + leftOffsetInSeconds}
            />
            {leftCapture ? (
              <Fragment>
                <div className={styles.captureCardCaptureViewer}>
                  <CaptureViewer
                    readOnly
                    showMaxPixelIntensityOverlay
                    capture={leftCapture}
                    initialStartTimeSeconds={
                      playbackState.seek + leftOffsetInSeconds
                    }
                    width={400}
                    configState={{
                      mutable_config: leftSensor.configState.config,
                      mutable_config_template:
                        leftSensor.configState.configTemplate,
                      config_status: leftSensor.configState.status,
                    }}
                    onVideoRefChanged={r => {
                      if (!leftRef.current) {
                        leftRef.current = r;
                        rerenderDueToRefChange();
                      }
                      if (onVideoRefChanged) {
                        onVideoRefChanged(r);
                      }
                    }}
                    showPoint={
                      leftPoint ? [leftPoint.x, leftPoint.y] : undefined
                    }
                    mpiOverlappingRegionOverlay={{
                      side: 'right' /* show on right side of left capture */,
                      width: overlappingRegionWidth / 2,
                      maxXOffsetMultiplier,
                    }}
                  />
                </div>
                <CaptureErrors capture={leftCapture} />
              </Fragment>
            ) : (
              noCaptureNode
            )}
          </div>
          <div className={styles.captureCardDistance}>
            <div className={styles.captureCardDistanceLabel}>
              {distanceInInches}&quot;
            </div>
            <div className={styles.captureCardDistanceLabelLine} />
          </div>
          <div className={styles.captureCard}>
            <CaptureCardHeader
              sensor={rightSensor}
              capture={rightCapture}
              seek={playbackState.seek + rightOffsetInSeconds}
            />
            {rightCapture ? (
              <Fragment>
                <div className={styles.captureCardCaptureViewer}>
                  <CaptureViewer
                    readOnly
                    showMaxPixelIntensityOverlay
                    capture={rightCapture}
                    initialStartTimeSeconds={
                      playbackState.seek + rightOffsetInSeconds
                    }
                    width={400}
                    configState={{
                      mutable_config: rightSensor.configState.config,
                      mutable_config_template:
                        rightSensor.configState.configTemplate,
                      config_status: rightSensor.configState.status,
                    }}
                    onVideoRefChanged={r => {
                      if (!rightRef.current) {
                        rightRef.current = r;
                        rerenderDueToRefChange();
                      }
                    }}
                    showPoint={
                      rightPoint ? [rightPoint.x, rightPoint.y] : undefined
                    }
                    mpiOverlappingRegionOverlay={{
                      side: 'left' /* show on left side of right capture */,
                      width: overlappingRegionWidth / 2,
                      maxXOffsetMultiplier,
                    }}
                  />
                </div>
                <CaptureErrors capture={rightCapture} />
              </Fragment>
            ) : (
              noCaptureNode
            )}
          </div>
        </div>
      </div>
      {rightCapture || leftCapture ? (
        <ViewerPlaybackControls
          keyboardShortcutsEnabled={keyboardShortcutsEnabled}
          playbackState={playbackState}
          onSetPlaybackState={setPlaybackState}
          tags={[]}
          tracks={tracks}
          trackLengthInSeconds={trackLengthInSeconds}
        />
      ) : null}
    </div>
  );
};

export default DualCaptureViewer;
